'use client';

import { c as _c } from "react/compiler-runtime";
import type { PaymentDiscountShape } from '@algoacademy/backend/modules/payment/enums';
import { useEffect } from 'react';
import type { MentoringOfferProduct } from '../../_forms/MentoringOfferForm/_helpers/mentoring-terms';
import { DiscountDurationField } from './_components/DiscountDurationField';
import { DiscountPreview } from './_components/DiscountPreview';
import { DiscountReasonField } from './_components/DiscountReasonField';
import { DiscountShapeToggle } from './_components/DiscountShapeToggle';
import { DiscountValueField } from './_components/DiscountValueField';
import { clampMentoringDiscountToProduct, type MentoringDiscountDraft, readDefaultDiscountValue, readInvoiceEffectivePriceZloty, readMaximumDiscountZloty, toMentoringDiscountInput } from './_helpers/discount-draft';
import { readDiscountPreviewFigures } from './_helpers/discount-preview';
export { EMPTY_MENTORING_DISCOUNT, type MentoringDiscountDraft, type MentoringDiscountInput, toMentoringDiscountInput } from './_helpers/discount-draft';
type DiscountControlsProps = {
  mentoringProduct: MentoringOfferProduct;
  value: MentoringDiscountDraft;
  onChange: (next: MentoringDiscountDraft) => void;
};

/**
 * The staff discount, from picking a shape to reading what the buyer will pay.
 * Fully controlled: the draft lives in whatever form drops this in, so one screen
 * can issue an offer with it and another can change a subscription with it.
 */
export function DiscountControls(t0) {
  const $ = _c(24);
  const {
    mentoringProduct,
    value,
    onChange
  } = t0;
  let t1;
  if ($[0] !== mentoringProduct || $[1] !== value) {
    t1 = clampMentoringDiscountToProduct({
      draft: value,
      mentoringProduct
    });
    $[0] = mentoringProduct;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const draft = t1;
  let t2;
  let t3;
  if ($[3] !== draft || $[4] !== onChange || $[5] !== value) {
    t2 = () => {
      if (draft !== value) {
        onChange(draft);
      }
    };
    t3 = [draft, value, onChange];
    $[3] = draft;
    $[4] = onChange;
    $[5] = value;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== draft || $[9] !== mentoringProduct || $[10] !== onChange) {
    t4 = shape => {
      if (shape === null) {
        onChange({
          ...draft,
          shape: null
        });
        return;
      }
      onChange(clampMentoringDiscountToProduct({
        draft: {
          ...draft,
          shape,
          value: readDefaultDiscountValue(shape)
        },
        mentoringProduct
      }));
    };
    $[8] = draft;
    $[9] = mentoringProduct;
    $[10] = onChange;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleShapeChange = t4;
  let t5;
  if ($[12] !== draft || $[13] !== handleShapeChange || $[14] !== mentoringProduct || $[15] !== onChange) {
    const discountInput = toMentoringDiscountInput(draft);
    let t6;
    if ($[17] !== mentoringProduct) {
      t6 = readInvoiceEffectivePriceZloty(mentoringProduct);
      $[17] = mentoringProduct;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    const invoicePriceZloty = t6;
    let t7;
    if ($[19] !== mentoringProduct) {
      t7 = readMaximumDiscountZloty(mentoringProduct);
      $[19] = mentoringProduct;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    const maximumZloty = t7;
    let t8;
    if ($[21] !== draft.shape || $[22] !== handleShapeChange) {
      t8 = <DiscountShapeToggle shape={draft.shape} onChange={handleShapeChange} />;
      $[21] = draft.shape;
      $[22] = handleShapeChange;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    t5 = <div className="flex w-full flex-col gap-4 text-left">{t8}{draft.shape === null ? null : <div className="space-y-4"><div className="grid grid-cols-1 items-stretch gap-x-6 gap-y-2 lg:grid-cols-2"><DiscountValueField shape={draft.shape} value={draft.value} maximumZloty={maximumZloty} invoicePriceZloty={invoicePriceZloty} onChange={nextValue => onChange({
            ...draft,
            value: nextValue
          })} /><DiscountDurationField periodCount={draft.periodCount} onChange={nextPeriodCount => onChange({
            ...draft,
            periodCount: nextPeriodCount
          })} /></div><DiscountReasonField reason={draft.reason} onChange={nextReason => onChange({
          ...draft,
          reason: nextReason
        })} />{discountInput === null ? null : <DiscountPreview figures={readDiscountPreviewFigures({
          mentoringProduct,
          discount: discountInput
        })} />}</div>}</div>;
    $[12] = draft;
    $[13] = handleShapeChange;
    $[14] = mentoringProduct;
    $[15] = onChange;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}